'use client';
import { useEffect, useState } from 'react';

interface ThemeDebugInfo {
  activeTheme: 'dark' | 'light' | 'unknown';
  systemPreference: 'dark' | 'light' | 'no-preference';
  storedPreference: string | null;
  htmlClass: string;
  effectiveSource: string;
}

export function ThemeDebug({ enabled = false }: { enabled?: boolean }) {
  const [info, setInfo] = useState<ThemeDebugInfo | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const updateInfo = () => {
      const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const systemLight = window.matchMedia('(prefers-color-scheme: light)').matches;
      const storedPreference = localStorage.getItem('theme');
      const htmlClass = document.documentElement.className;
      const hasDarkClass = document.documentElement.classList.contains('dark');

      let effectiveSource = 'Unknown';
      if (storedPreference) {
        effectiveSource = `localStorage ("${storedPreference}")`;
      } else if (systemDark || systemLight) {
        effectiveSource = `System preference (${systemDark ? 'dark' : 'light'})`;
      } else {
        effectiveSource = 'Default fallback';
      }

      setInfo({
        activeTheme: hasDarkClass ? 'dark' : 'light',
        systemPreference: systemDark ? 'dark' : systemLight ? 'light' : 'no-preference',
        storedPreference,
        htmlClass,
        effectiveSource,
      });
    };

    updateInfo();

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', updateInfo);

    const observer = new MutationObserver(updateInfo);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    return () => {
      mediaQuery.removeEventListener('change', updateInfo);
      observer.disconnect();
    };
  }, [enabled]);

  if (!enabled || !info) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 rounded-lg border border-neutral-300 bg-white p-3 font-mono text-xs shadow-lg dark:border-neutral-700 dark:bg-neutral-900">
      <div className="mb-2 font-semibold text-neutral-900 dark:text-neutral-100">
        Theme Debug
      </div>
      <table className="text-neutral-700 dark:text-neutral-300">
        <tbody>
          <tr>
            <td className="pr-3 text-neutral-500">Active:</td>
            <td className="font-medium">{info.activeTheme}</td>
          </tr>
          <tr>
            <td className="pr-3 text-neutral-500">System:</td>
            <td>{info.systemPreference}</td>
          </tr>
          <tr>
            <td className="pr-3 text-neutral-500">Stored:</td>
            <td>{info.storedPreference ?? '(none)'}</td>
          </tr>
          <tr>
            <td className="pr-3 text-neutral-500">HTML class:</td>
            <td className="max-w-32 truncate">{info.htmlClass || '(empty)'}</td>
          </tr>
          <tr>
            <td className="pr-3 pt-2 text-neutral-500">Source:</td>
            <td className="pt-2 font-semibold text-blue-600 dark:text-blue-400">
              {info.effectiveSource}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}